import { useEffect, useMemo } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { ConfigProvider, theme, Result, Button } from 'antd';
import WebApp from '@twa-dev/sdk';
import { useTranslation } from 'react-i18next';
import ClientShell from './pages/client/ClientShell';
import StaffDashboardPage from './pages/StaffDashboardPage';
import BusinessManagementPage from './pages/BusinessManagementPage';
import BusinessListPage from './pages/BusinessListPage';
import OwnerOnboardingPage from './pages/owner-onboarding';
import LogViewer from './components/LogViewer';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useAuthStore } from './store/authStore';
import { useLanguageStore } from './store/languageStore';
import { getAntdLocale } from './i18n/antdLocales';
import type { SessionDto } from './types/auth';
import type { AuthErrorReason } from './store/authStore';

function SplashScreen() {
  return (
    <div className="bl-splash-wrapper">
      <div
        className="bl-splash-clock"
        role="status"
        aria-label="Yuklanmoqda"
      >
        <div className="bl-splash-hand bl-splash-hand-hour" />
        <div className="bl-splash-hand bl-splash-hand-min" />
      </div>
    </div>
  );
}

function BotRedirectScreen({ reason }: { reason: AuthErrorReason | 'NEEDS_PHONE' }) {
  const { t } = useTranslation('common');

  const subTitle =
    reason === 'NO_INIT_DATA'
      ? t('auth.noInitData')
      : reason === 'NO_SLUG'
        ? t('auth.noSlug')
        : reason === 'NEEDS_PHONE'
          ? t('auth.needsPhone')
          : t('auth.authFailed');

  // The previous "open via bot" button opened https://t.me/<botUsername>, but
  // getBotUsername() returns the tenant slug (not a real Telegram username),
  // so it could navigate to an unrelated/foreign chat. The only safe action
  // here is to close the WebApp.
  return (
    <Result
      status="info"
      title={t('auth.openViaBotTitle')}
      subTitle={subTitle}
      extra={
        <Button type="primary" onClick={() => WebApp.close()}>
          {t('auth.close')}
        </Button>
      }
    />
  );
}

/**
 * Renders the correct page based on server-driven routing.target.
 * FE never computes routing from role/tenantSlug — only reads routing.target from session.
 */
function RouteRenderer({ session }: { session: SessionDto }) {
  const { routing, ownedTenants } = session;

  switch (routing.target) {
    case 'CLIENT_HOME':
      return <ClientShell />;
    case 'PROVIDER_PANEL':
      return <StaffDashboardPage />;
    case 'OWNER_DASHBOARD':
      return ownedTenants.length > 1 ? <BusinessListPage /> : <BusinessManagementPage />;
    case 'OWNER_ONBOARDING':
      return <OwnerOnboardingPage />;
    case 'NEEDS_PHONE':
      return <BotRedirectScreen reason="NEEDS_PHONE" />;
    default:
      return <BotRedirectScreen reason="AUTH_FAILED" />;
  }
}

function App() {
  const { status, errorReason, session, bootstrap } = useAuthStore();
  const { currentLanguage, isInitialized } = useLanguageStore();
  const isDarkTheme = WebApp.colorScheme === 'dark';

  const themeConfig = useMemo(
    () => ({
      algorithm: isDarkTheme ? theme.darkAlgorithm : theme.defaultAlgorithm,
      token: {
        colorPrimary: isDarkTheme ? '#8774e1' : '#3390ec',
        borderRadius: 12,
        borderRadiusLG: 20,
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'SF Pro', system-ui, 'Segoe UI Variable', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        fontSize: 15,
        lineHeight: 1.47,
        controlHeight: 44,
      },
      components: {
        Card: { borderRadiusLG: 20 },
        Tag: { borderRadiusSM: 999 },
        Button: { borderRadius: 12, controlHeight: 44, fontWeight: 600 },
        Typography: { titleMarginBottom: '0.5em', titleMarginTop: '0' },
      },
    }),
    [isDarkTheme]
  );

  useEffect(() => {
    document.body.classList.toggle('dark', isDarkTheme);
    document.body.classList.toggle('light', !isDarkTheme);
    WebApp.expand();
    bootstrap();

    // When API interceptor clears stale tokens (401 + failed refresh),
    // reset to error state so user sees BotRedirectScreen.
    const handleAuthLogout = () => {
      useAuthStore.setState({ status: 'error', errorReason: 'AUTH_FAILED', session: null, user: null });
    };
    window.addEventListener('auth:logout', handleAuthLogout);
    return () => window.removeEventListener('auth:logout', handleAuthLogout);
  }, []);

  if (status === 'idle' || status === 'pending' || (status === 'authenticated' && !isInitialized)) {
    return (
      <ConfigProvider theme={themeConfig} locale={getAntdLocale(currentLanguage)}>
        <SplashScreen />
      </ConfigProvider>
    );
  }

  if (status === 'error') {
    return (
      <ConfigProvider theme={themeConfig} locale={getAntdLocale(currentLanguage)}>
        <BotRedirectScreen reason={errorReason} />
      </ConfigProvider>
    );
  }

  // status === 'authenticated'
  return (
    <ConfigProvider theme={themeConfig} locale={getAntdLocale(currentLanguage)}>
      <BrowserRouter>
        <ErrorBoundary>
          <RouteRenderer session={session!} />
        </ErrorBoundary>
        {(import.meta.env.DEV || localStorage.getItem('__debug') === '1') && <LogViewer />}
      </BrowserRouter>
    </ConfigProvider>
  );
}

export default App;
